import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faSeedling,
  faQuestionCircle,
  faCalendarAlt,
  faUserSlash,
  faBell,
  faAddressBook,
  faCheckCircle,
  faClock,
  faPause,
  faArrowUp,
  faArrowDown,
  faMinus,
  faPlus,
  faCheck,
  faTrash,
  faPhone,
  faHistory,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  CartesianGrid,
  LabelList,
  defs,
  linearGradient,
} from "recharts";
import getPendingIssues from "../IssueHomePage/getPendingIssues";
import getRemindersApi     from "../Reminders/getReminders";
import addReminderApi      from "../Reminders/addReminder";
import completeReminderApi from "../Reminders/completeReminder";
import deleteReminderApi   from "../Reminders/deleteReminder";
import reopenReminderApi   from "../Reminders/reopenReminder";
import axios from "axios";
import moment from "moment";

// ─── Colors ──────────────────────────────────────────────────────────────────
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#eef4f0",
  border: "#d0e8d8",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  red: "#e74c3c",
  orange: "#f39c12",
  blue: "#3b82f6",
  gold: "#4CAF50",
  silver: "#2196F3",
  bronze: "#FFC107",
  raw: "#F44336",
  dashbg: "#f0faf2",
};

// ─── Helper: group records by last-6-months ───────────────────────────────────
const groupByMonth = (items, dateField, valueKey) => {
  const counts = {};
  items.forEach((item) => {
    if (item[dateField]) {
      const key = moment(item[dateField]).format("MMM YYYY");
      counts[key] = (counts[key] || 0) + 1;
    }
  });
  const result = [];
  for (let i = 5; i >= 0; i--) {
    const label = moment().subtract(i, "months").format("MMM");
    const key = moment().subtract(i, "months").format("MMM YYYY");
    result.push({ month: label, [valueKey]: counts[key] || 0 });
  }
  return result;
};


const QUERY_WEEKLY = [
  { week: "01-07", solved: 80, pending: 20, hold: 5 },
  { week: "08-14", solved: 120, pending: 35, hold: 8 },
  { week: "15-21", solved: 170, pending: 45, hold: 12 },
  { week: "22-28", solved: 160, pending: 40, hold: 10 },
];

const CROP_PALETTE = [C.bright, C.blue, C.gold, C.orange, C.red, "#8d7355", "#8e44ad", "#16a085", "#95a5a6", "#2c3e50"];

const REMINDERS = [
  {
    date: "TODAY",
    time: "2:30 PM",
    title: "Pest Attack Consultation Required",
    farmer: "Rajesh Kumar (Cotton Field)",
    urgency: "immediateaction",
  },
  {
    date: "TODAY",
    time: "4:00 PM",
    title: "Soil Testing Report Review",
    farmer: "Priya Desai (Tomato Farm)",
    urgency: "immediateaction",
  },
  {
    date: "TOMORROW",
    time: "10:00 AM",
    title: "Field Inspection Visit",
    farmer: "Vikram Singh (Wheat Field)",
    urgency: "moderatepriority",
  },
  {
    date: "TOMORROW",
    time: "2:00 PM",
    title: "Follow-up on Fertilizer Order",
    farmer: "Akshara Iyer (Soybean Farm)",
    urgency: "moderatepriority",
  },
  {
    date: "03/27",
    time: "9:00 AM",
    title: "Quarterly Performance Review",
    farmer: "Ramesh Rao (Sugarcane Field)",
    urgency: "lowpriority",
  },
];

const WEATHER_DATA = {
  location: "Nagpur, Maharashtra",
  temp: 32,
  feelsLike: 35,
  condition: "Partly Cloudy",
  humidity: 65,
  wind: 12,
  visibility: 8,
  forecast: [
    { day: "Mon", icon: "⛅", hi: 33, lo: 24 },
    { day: "Tue", icon: "🌧️", hi: 28, lo: 22 },
    { day: "Wed", icon: "☀️", hi: 35, lo: 25 },
    { day: "Thu", icon: "⛅", hi: 31, lo: 23 },
  ],
};

const MARKET_RATES = [
  { crop: "Cotton", price: 6840, unit: "₹/qtl", change: +2.3, icon: "🌾" },
  { crop: "Tomato", price: 1200, unit: "₹/qtl", change: -5.1, icon: "🍅" },
  { crop: "Soybean", price: 4250, unit: "₹/qtl", change: +1.8, icon: "🫘" },
  { crop: "Wheat", price: 2150, unit: "₹/qtl", change: +0.5, icon: "🌿" },
  { crop: "Sugarcane", price: 310, unit: "₹/qtl", change: -1.2, icon: "🎋" },
  { crop: "Onion", price: 2800, unit: "₹/qtl", change: +8.4, icon: "🧅" },
];

const AGRI_NEWS = [
  {
    title: "Govt announces MSP hike for Kharif crops — wheat, paddy & soybean benefit",
    time: "2h ago",
    tag: "Agri",
  },
  {
    title: "Soybean prices rise 8% as export demand from Southeast Asia surges",
    time: "4h ago",
    tag: "Crops",
  },
  {
    title: "Best practices for drip irrigation in cotton farming during dry season",
    time: "6h ago",
    tag: "Farming",
  },
  {
    title: "Onion crop pricing expected to stabilize after Maharashtra harvest",
    time: "1d ago",
    tag: "Crops",
  },
  {
    title: "ICAR releases new pest-resistant sugarcane variety for Deccan region",
    time: "1d ago",
    tag: "Farming",
  },
  {
    title: "PM Kisan installment disbursed — check eligibility for next cycle",
    time: "2d ago",
    tag: "Agri",
  },
  {
    title: "सोयाबीन व कापूस उत्पादकांसाठी शासनाची नवीन मदत योजना जाहीर",
    time: "3h ago",
    tag: "मराठी",
  },
  {
    title: "खरीप हंगाम २०२५ — बियाणे व खते उपलब्धतेबाबत कृषी विभागाचे आवाहन",
    time: "8h ago",
    tag: "मराठी",
  },
  {
    title: "फसल बीमा योजना के तहत किसानों को मिलेगा बड़ा मुआवजा",
    time: "5h ago",
    tag: "हिंदी",
  },
  {
    title: "गेहूं और धान की एमएसपी में वृद्धि — किसानों को सीधा लाभ",
    time: "1d ago",
    tag: "हिंदी",
  },
];

const TAG_COLORS = {
  Agri:    { bg: "#fff3e0", text: "#b76a00" },
  Farming: { bg: "#e8f5ee", text: "#2d7a45" },
  Crops:   { bg: "#fce4ec", text: "#c62828" },
  "मराठी": { bg: "#fff8e1", text: "#e65100" },
  "हिंदी": { bg: "#f3e5f5", text: "#7b1fa2" },
};

const MOCK_DIALER = [
  { name: "Rajesh K.", status: "Active", mobile: "9876543210", initials: "RK" },
  { name: "Priya D.", status: "Active", mobile: "9876543211", initials: "PD" },
  { name: "Vikram S.", status: "Active", mobile: "9876543212", initials: "VS" },
  {
    name: "Akshara I.",
    status: "Active",
    mobile: "9876543213",
    initials: "AI",
  },
  { name: "Ramesh R.", status: "Busy", mobile: "9876543214", initials: "RR" },
  { name: "Neha S.", status: "Active", mobile: "9876543215", initials: "NS" },
];

const STATUS_STYLE = {
  New: { bg: "#f0f0f0", text: "#6c757d" },
  "In Progress": { bg: "#e3f0ff", text: "#3b82f6" },
  SolutionProvided: { bg: "#e6f4f0", text: "#2d6a4f" },
  Done: { bg: "#e8f5e9", text: "#2e7d32" },
  "On Hold": { bg: "#fff3e0", text: "#e65100" },
  Rejected: { bg: "#fce4ec", text: "#c62828" },
  "In Follow": { bg: "#f3e5f5", text: "#7b1fa2" },
};

const STATUS_LABEL = {
  SolutionProvided: "SOLUTION PROVIDED",
  "In Progress": "IN PROGRESS",
  New: "NEW",
  Done: "DONE",
  "On Hold": "ON HOLD",
  Rejected: "REJECTED",
  "In Follow": "IN FOLLOW",
};

// ─── Reusable layout pieces ───────────────────────────────────────────────────
const SectionHeader = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      margin: "1.5rem 0 0.75rem",
    }}
  >
    <div
      style={{
        width: 4,
        height: 20,
        backgroundColor: C.bright,
        borderRadius: 2,
      }}
    />
    <span style={{ fontSize: "1.1rem" }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>
      {label}
    </span>
  </div>
);

const CardHdr = ({ title, icon }) => (
  <div
    style={{
      backgroundColor: C.dark,
      color: "#fff",
      padding: "0.65rem 1rem",
      borderRadius: "12px 12px 0 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{title}</span>
    <span style={{ fontSize: "1.1rem" }}>{icon}</span>
  </div>
);

const DashCard = ({ children, style }) => (
  <div
    style={{
      backgroundColor: C.white,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      boxShadow: "0 4px 16px rgba(20,50,30,0.07)",
      overflow: "hidden",
      height: "100%",
      ...style,
    }}
  >
    {children}
  </div>
);

// ─── Shimmer / Skeleton ───────────────────────────────────────────────────────
const ShimmerStyle = () => (
  <style>{`
    @keyframes mwa-shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
    .mwa-shimmer {
      background: linear-gradient(90deg, #deeae3 25%, #edf5f0 50%, #deeae3 75%);
      background-size: 1200px 100%;
      animation: mwa-shimmer 1.5s infinite linear;
    }
  `}</style>
);

const Sh = ({ h = 12, w = "100%", r = 4, sx = {} }) => (
  <div
    className="mwa-shimmer"
    style={{ height: h, width: w, borderRadius: r, flexShrink: 0, ...sx }}
  />
);

const StatCardSkeleton = () => (
  <div
    style={{
      backgroundColor: C.white,
      borderRadius: 12,
      padding: "1.1rem 1rem",
      border: `1px solid ${C.border}`,
      borderLeft: `4px solid ${C.border}`,
      boxShadow: "0 4px 12px rgba(20,50,30,0.06)",
      height: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        marginBottom: "0.75rem",
      }}
    >
      <Sh h={34} w={34} r={17} />
      <Sh h={10} w="55%" />
    </div>
    <Sh h={34} w="45%" sx={{ marginBottom: 10 }} />
    <Sh h={9} w="75%" />
  </div>
);

const ChartLineSkeleton = ({ title, icon }) => (
  <DashCard>
    <CardHdr title={title} icon={icon} />
    <div style={{ padding: "1rem 0.75rem 0.75rem" }}>
      <Sh h={170} r={6} sx={{ marginBottom: 12 }} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          paddingTop: "0.6rem",
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <Sh h={28} w="28%" r={6} />
        <Sh h={28} w="28%" r={6} />
        <Sh h={28} w="28%" r={6} />
      </div>
    </div>
  </DashCard>
);

const ChartBarSkeleton = ({ title, icon }) => (
  <DashCard>
    <CardHdr title={title} icon={icon} />
    <div style={{ padding: "0.75rem 0.5rem 0.75rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 6,
          height: 180,
          paddingBottom: 8,
        }}
      >
        {[110, 68, 135, 82, 150, 95].map((h, i) => (
          <Sh key={i} h={h} w="14%" sx={{ borderRadius: "4px 4px 0 0" }} />
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingTop: "0.5rem",
          borderTop: `1px solid ${C.border}`,
          marginTop: "0.5rem",
        }}
      >
        <Sh h={9} w="38%" />
        <Sh h={9} w="22%" />
      </div>
    </div>
  </DashCard>
);

const DialerSkeleton = () => (
  <DashCard>
    <CardHdr title="Quick Farmer Dialer" icon="☎️" />
    <div style={{ padding: "0.75rem" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "0.5rem",
          marginBottom: "0.75rem",
        }}
      >
        {Array(6)
          .fill(0)
          .map((_, i) => (
            <div
              key={i}
              style={{
                padding: "0.6rem 0.25rem",
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 7,
              }}
            >
              <Sh h={42} w={42} r={21} />
              <Sh h={9} w="70%" />
              <Sh h={16} w="50%" r={8} />
            </div>
          ))}
      </div>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Sh h={38} w="50%" r={8} />
        <Sh h={38} w="50%" r={8} />
      </div>
    </div>
  </DashCard>
);

// ─── Stats Cards ──────────────────────────────────────────────────────────────
const trendIcon = { up: faArrowUp, down: faArrowDown, flat: faMinus };
const trendColor = { up: "#2e7d32", down: C.red, flat: C.textGr };

const StatCard = ({
  icon,
  label,
  value,
  sub,
  subIcon,
  trend,
  iconBg,
  iconColor,
}) => (
  <div
    style={{
      backgroundColor: C.white,
      borderRadius: 12,
      padding: "1.1rem 1rem",
      border: `1px solid ${C.border}`,
      borderLeft: `4px solid ${C.bright}`,
      boxShadow: "0 4px 12px rgba(20,50,30,0.06)",
      height: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        marginBottom: "0.5rem",
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          backgroundColor: iconBg || "#e8f5ee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon
          icon={icon}
          style={{ color: iconColor || C.mid, fontSize: "0.9rem" }}
        />
      </div>
      <span style={{ fontSize: "0.78rem", color: C.textGr, fontWeight: 500 }}>
        {label}
      </span>
    </div>
    <div
      style={{
        fontSize: "2rem",
        fontWeight: 800,
        color: C.textDk,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.3rem",
        marginTop: "0.35rem",
      }}
    >
      {subIcon && (
        <FontAwesomeIcon
          icon={subIcon}
          style={{ fontSize: "0.65rem", color: C.textGr }}
        />
      )}
      {trend && (
        <FontAwesomeIcon
          icon={trendIcon[trend] || faMinus}
          style={{ fontSize: "0.6rem", color: trendColor[trend] || C.textGr }}
        />
      )}
      <span style={{ fontSize: "0.73rem", color: C.textGr }}>{sub}</span>
    </div>
  </div>
);

// ─── Chart: Monthly Farmer Registration ───────────────────────────────────────
const RegistrationTrendCard = ({ data = [], loading = false }) => {
  if (loading)
    return <ChartLineSkeleton title="Monthly Farmer Registration" icon="📈" />;
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  const latest = data[data.length - 1]?.count ?? 0;
  const prev = data[data.length - 2]?.count ?? 0;
  const growth = prev > 0 ? (((latest - prev) / prev) * 100).toFixed(0) : "—";
  return (
    <DashCard>
      <CardHdr title="Monthly Farmer Registration" icon="📈" />
      <div style={{ padding: "1rem 0.75rem 0.5rem" }}>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e8f0ec" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="count"
              name="Farmers"
              stroke={C.bright}
              strokeWidth={2.5}
              dot={{ r: 3, fill: C.bright, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            padding: "0.5rem 0 0.25rem",
            borderTop: `1px solid ${C.border}`,
            marginTop: "0.25rem",
          }}
        >
          {[
            { label: "This Month", val: latest.toLocaleString() },
            {
              label: "Growth",
              val: growth !== "—" ? `${growth > 0 ? "+" : ""}${growth}%` : "—",
            },
            { label: "6-Mo Total", val: total.toLocaleString() },
          ].map(({ label, val }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{ fontWeight: 700, fontSize: "0.9rem", color: C.mid }}
              >
                {val}
              </div>
              <div style={{ fontSize: "0.65rem", color: C.textGr }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashCard>
  );
};

// ─── Chart: Monthly Query Count ───────────────────────────────────────────────
const MonthlyQueryCountCard = ({ data = [], loading = false }) => {
  if (loading)
    return <ChartLineSkeleton title="Monthly Query Count" icon="📋" />;
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  const latest = data[data.length - 1]?.count || 0;
  const prev = data[data.length - 2]?.count || 0;
  const growth = prev > 0 ? (((latest - prev) / prev) * 100).toFixed(0) : "—";
  return (
    <DashCard>
      <CardHdr title="Monthly Query Count" icon="📋" />
      <div style={{ padding: "1rem 0.75rem 0.5rem" }}>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e8f0ec" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="count"
              name="Queries"
              stroke={C.blue}
              strokeWidth={2.5}
              dot={{ r: 3, fill: C.blue, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            padding: "0.5rem 0 0.25rem",
            borderTop: `1px solid ${C.border}`,
            marginTop: "0.25rem",
          }}
        >
          {[
            { label: "This Month", val: latest.toLocaleString() },
            {
              label: "Growth",
              val: growth !== "—" ? `${growth > 0 ? "+" : ""}${growth}%` : "—",
            },
            { label: "6-Mo Total", val: total.toLocaleString() },
          ].map(({ label, val }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{ fontWeight: 700, fontSize: "0.9rem", color: C.blue }}
              >
                {val}
              </div>
              <div style={{ fontSize: "0.65rem", color: C.textGr }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashCard>
  );
};

// ─── Chart: Monthly Appointment Booking ───────────────────────────────────────
const MonthlyAppointmentCard = ({ data = [], loading = false }) => {
  if (loading)
    return <ChartLineSkeleton title="Monthly Appointment Booking" icon="📅" />;
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  const latest = data[data.length - 1]?.count || 0;
  const prev = data[data.length - 2]?.count || 0;
  const growth = prev > 0 ? (((latest - prev) / prev) * 100).toFixed(0) : "—";
  return (
    <DashCard>
      <CardHdr title="Monthly Appointment Booking" icon="📅" />
      <div style={{ padding: "1rem 0.75rem 0.5rem" }}>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e8f0ec" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="count"
              name="Appointments"
              stroke={C.gold}
              strokeWidth={2.5}
              dot={{ r: 3, fill: C.gold, strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            padding: "0.5rem 0 0.25rem",
            borderTop: `1px solid ${C.border}`,
            marginTop: "0.25rem",
          }}
        >
          {[
            { label: "This Month", val: latest.toLocaleString() },
            {
              label: "Growth",
              val: growth !== "—" ? `${growth > 0 ? "+" : ""}${growth}%` : "—",
            },
            { label: "6-Mo Total", val: total.toLocaleString() },
          ].map(({ label, val }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{ fontWeight: 700, fontSize: "0.9rem", color: C.gold }}
              >
                {val}
              </div>
              <div style={{ fontSize: "0.65rem", color: C.textGr }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashCard>
  );
};

// ─── Chart: State-wise Farmer Count ───────────────────────────────────────────
const StatewiseFarmerCard = ({ data = [], loading = false }) => {
  if (loading || !data.length) {
    return <ChartBarSkeleton title="State-wise Farmer Count" icon="🗺️" />;
  }

  const rows = data;
  const total = rows.reduce((s, d) => s + d.count, 0);

  const chartData = rows.map((r) => ({
    ...r,
    active: r.active ?? r.count,
    inactive: r.inactive ?? 0,
    label: r.state.length > 9 ? r.state.slice(0, 8) + "…" : r.state,
  }));

  return (
    <DashCard>
      <CardHdr title="State-wise Farmer Count" icon="🗺️" />
      <div style={{ padding: "0.75rem 0.5rem 0.5rem" }}>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart
            data={chartData}
            margin={{ top: 16, right: 8, left: -18, bottom: 4 }}
            barSize={22}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e8f0ec"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: C.textDk, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fill: C.textGr }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) =>
                v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
              }
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontSize: 11,
              }}
              labelFormatter={(label, payload) =>
                payload?.[0]?.payload?.state || label
              }
              formatter={(value, name) => [
                `${value.toLocaleString()}`,
                name === "active" ? "Active" : "Inactive",
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: 9 }}
              formatter={(v) => (v === "active" ? "Active" : "Inactive")}
            />
            <Bar
              dataKey="active"
              stackId="a"
              fill={C.bright}
              name="active"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="inactive"
              stackId="a"
              fill={C.red}
              name="inactive"
              radius={[4, 4, 0, 0]}
            >
              <LabelList
                dataKey="count"
                position="top"
                style={{ fontSize: 9, fill: C.textGr }}
                formatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "0.4rem 0.5rem 0",
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <span style={{ fontSize: "0.72rem", color: C.textGr }}>
            {rows.length} state{rows.length !== 1 ? "s" : ""} covered
          </span>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: C.mid }}>
            Total: {total.toLocaleString()}
          </span>
        </div>
      </div>
    </DashCard>
  );
};

// ─── Chart: Category Distribution ─────────────────────────────────────────────
const RADIAN = Math.PI / 180;
const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
  if (!value) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
    >
      {value}
    </text>
  );
};

const CategoryDistributionCard = () => {
  const [dist, setDist] = useState({ gold: 0, silver: 0, bronze: 0, raw: 0, loaded: false });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/dashboard/farmerCategoryDistribution`)
      .then((res) => {
        const { gold, silver, bronze, raw } = res.data;
        setDist({ gold: gold || 0, silver: silver || 0, bronze: bronze || 0, raw: raw || 0, loaded: true });
      })
      .catch(() => setDist((d) => ({ ...d, loaded: true })));
  }, []);

  const { gold, silver, bronze, raw, loaded } = dist;

  const chartData = [
    { name: "Gold Farmers",   value: gold,   color: C.gold   },
    { name: "Silver Farmers", value: silver, color: C.silver },
    { name: "Bronze Farmers", value: bronze, color: C.bronze },
    { name: "Raw Farmers",    value: raw,    color: C.raw    },
  ].filter((d) => d.value > 0);

  return (
    <DashCard>
      <CardHdr title="Farmer Category Distribution" icon="🌾" />
      <div style={{ padding: "0.75rem" }}>
        {!loaded ? (
          <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: C.textGr, fontSize: "0.82rem" }}>
            Loading…
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  labelLine={false}
                  label={renderLabel}
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, name) => [v, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.25rem" }}>
              {[
                { name: "Gold Farmers",   color: C.gold   },
                { name: "Silver Farmers", color: C.silver },
                { name: "Bronze Farmers", color: C.bronze },
                { name: "Raw Farmers",    color: C.raw    },
              ].map((d) => (
                <div key={d.name} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: d.color }} />
                  <span style={{ fontSize: "0.72rem", color: C.textGr }}>{d.name}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-around", paddingTop: "0.5rem", borderTop: `1px solid ${C.border}` }}>
              {[
                { val: gold,   label: "Gold Farmers",   color: C.gold   },
                { val: silver, label: "Silver Farmers", color: C.silver },
                { val: bronze, label: "Bronze Farmers", color: C.bronze },
                { val: raw,    label: "Raw Farmers",    color: C.raw    },
              ].map(({ val, label, color }) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem", color }}>{val}</div>
                  <div style={{ fontSize: "0.68rem", color: C.textGr }}>{label}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashCard>
  );
};

// ─── Chart: Crop Query Status ─────────────────────────────────────────────────
const CropQueryStatusCard = ({ solved, pending, onHold }) => (
  <DashCard>
    <CardHdr title="Crop Query Status" icon="📋" />
    <div style={{ padding: "0.75rem 0.5rem 0.5rem" }}>
      <ResponsiveContainer width="100%" height={170}>
        <BarChart
          data={QUERY_WEEKLY}
          margin={{ top: 5, right: 5, left: -25, bottom: 0 }}
          barSize={10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e8f0ec" />
          <XAxis
            dataKey="week"
            tick={{ fontSize: 10, fill: C.textGr }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: C.textGr }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
          <Bar dataKey="solved" fill={C.bright} radius={[3, 3, 0, 0]} />
          <Bar dataKey="pending" fill={C.blue} radius={[3, 3, 0, 0]} />
          <Bar dataKey="hold" fill={C.red} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.3rem",
          padding: "0.5rem 0.5rem 0.25rem",
          borderTop: `1px solid ${C.border}`,
          marginTop: "0.25rem",
        }}
      >
        {[
          {
            icon: faCheckCircle,
            color: C.bright,
            label: "Solved",
            val: solved || 0,
          },
          { icon: faClock, color: C.blue, label: "Pending", val: pending || 0 },
          { icon: faPause, color: C.red, label: "Hold", val: onHold || 0 },
        ].map(({ icon, color, label, val }) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
            >
              <div
                style={{
                  backgroundColor: color + "22",
                  borderRadius: 4,
                  padding: "1px 6px",
                }}
              >
                <FontAwesomeIcon
                  icon={icon}
                  style={{ color, fontSize: "0.7rem" }}
                />
                <span
                  style={{
                    fontSize: "0.72rem",
                    color,
                    fontWeight: 600,
                    marginLeft: 4,
                  }}
                >
                  {label}
                </span>
              </div>
            </div>
            <span
              style={{ fontWeight: 700, fontSize: "0.85rem", color: C.textDk }}
            >
              {val}
            </span>
          </div>
        ))}
      </div>
    </div>
  </DashCard>
);

// ─── Chart: Crop-wise Farmer Data ─────────────────────────────────────────────
const CropFarmerDataCard = ({ data = [], loading = false }) => {
  const [selectedCrop, setSelectedCrop] = useState(null);
  if (loading) return <ChartBarSkeleton title="Crop-wise Farmer Data" icon="🌿" />;
  if (!data.length) {
    return (
      <DashCard>
        <CardHdr title="Crop-wise Farmer Data" icon="🌿" />
        <div style={{ padding: "2.5rem 1rem", textAlign: "center", color: C.textGr, fontSize: "0.85rem" }}>
          No crop data available.
        </div>
      </DashCard>
    );
  }

  const activeCrop = (selectedCrop && data.find((d) => d.crop === selectedCrop))
    ? selectedCrop
    : data[0].crop;
  const cropEntry = data.find((d) => d.crop === activeCrop);
  const cropColor = CROP_PALETTE[data.findIndex((d) => d.crop === activeCrop) % CROP_PALETTE.length];
  const stateRows = cropEntry?.states?.slice(0, 9) || [];
  const maxCount = Math.max(...stateRows.map((s) => s.count), 1);

  return (
    <DashCard>
      <CardHdr title="Crop-wise Farmer Data" icon="🌿" />
      <div style={{ padding: "0.75rem 0.75rem 0.5rem" }}>
        {/* Crop selector pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.75rem" }}>
          {data.slice(0, 10).map(({ crop, total }, i) => {
            const active = crop === activeCrop;
            const color = CROP_PALETTE[i % CROP_PALETTE.length];
            return (
              <button
                key={crop}
                onClick={() => setSelectedCrop(crop)}
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  padding: "3px 9px",
                  borderRadius: 12,
                  border: `1.5px solid ${active ? color : C.border}`,
                  backgroundColor: active ? color : C.white,
                  color: active ? "#fff" : C.textGr,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {crop}
                <span style={{ marginLeft: 4, opacity: 0.8 }}>
                  {total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total}
                </span>
              </button>
            );
          })}
        </div>

        {/* State breakdown — 2-column grid to fit 9 states */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.45rem 0.9rem" }}>
          {stateRows.map(({ state, count }) => (
            <div key={state}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.15rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 500, color: C.textDk,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "68%" }}>
                  {state}
                </span>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, color: C.textDk }}>
                  {count.toLocaleString()}
                </span>
              </div>
              <div style={{ height: 5, backgroundColor: "#f0f5f2", borderRadius: 3, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${(count / maxCount) * 100}%`,
                  backgroundColor: cropColor,
                  borderRadius: 3,
                  transition: "width 0.5s ease",
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.45rem",
          borderTop: `1px solid ${C.border}`, marginTop: "0.35rem" }}>
          <span style={{ fontSize: "0.7rem", color: C.textGr }}>
            {cropEntry?.states?.length || 0} state{(cropEntry?.states?.length || 0) !== 1 ? "s" : ""}
          </span>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, color: C.mid }}>
            Total: {cropEntry?.total?.toLocaleString()}
          </span>
        </div>
      </div>
    </DashCard>
  );
};

// ─── WMO weather code → emoji + label ────────────────────────────────────────
const WMO_CODE = {
  0:  { icon: "☀️",  label: "Clear sky" },
  1:  { icon: "🌤️", label: "Mainly clear" },
  2:  { icon: "⛅",  label: "Partly cloudy" },
  3:  { icon: "☁️",  label: "Overcast" },
  45: { icon: "🌫️", label: "Fog" },
  48: { icon: "🌫️", label: "Icy fog" },
  51: { icon: "🌦️", label: "Light drizzle" },
  53: { icon: "🌦️", label: "Drizzle" },
  55: { icon: "🌧️", label: "Heavy drizzle" },
  61: { icon: "🌧️", label: "Light rain" },
  63: { icon: "🌧️", label: "Rain" },
  65: { icon: "🌧️", label: "Heavy rain" },
  71: { icon: "🌨️", label: "Light snow" },
  73: { icon: "🌨️", label: "Snow" },
  75: { icon: "❄️",  label: "Heavy snow" },
  80: { icon: "🌦️", label: "Rain showers" },
  81: { icon: "🌦️", label: "Heavy showers" },
  82: { icon: "⛈️",  label: "Violent showers" },
  95: { icon: "⛈️",  label: "Thunderstorm" },
  96: { icon: "⛈️",  label: "Thunderstorm + hail" },
  99: { icon: "⛈️",  label: "Heavy thunderstorm" },
};
const wmoInfo = (code) => WMO_CODE[code] || { icon: "🌡️", label: "Unknown" };

const WIND_DIRS = ["N","NE","E","SE","S","SW","W","NW"];
const degToDir  = (deg) => WIND_DIRS[Math.round(deg / 45) % 8];
const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const WeatherCard = () => {
  const storedUser  = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultLoc  = storedUser.district || storedUser.state || "Pune";

  const [inputVal,    setInputVal]    = useState(defaultLoc);
  const [suggestions, setSuggestions] = useState([]);
  const [location,    setLocation]    = useState(null);
  const [weather,     setWeather]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [fetchedAt,   setFetchedAt]   = useState(null);

  const fetchWeather = async (searchName) => {
    if (!searchName) return;
    setLoading(true);
    setError("");
    setSuggestions([]);
    try {
      const geoRes  = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchName)}&count=1&language=en&format=json`
      );
      const geoData = await geoRes.json();
      const loc     = geoData?.results?.[0];
      if (!loc) { setError("Location not found. Try a different name."); setLoading(false); return; }

      setLocation({ name: loc.name, lat: loc.latitude, lon: loc.longitude, admin1: loc.admin1 || "", country: loc.country || "" });

      const wRes  = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_direction_10m,is_day` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum` +
        `&timezone=auto&forecast_days=5`
      );
      const wData = await wRes.json();
      setWeather(wData);
      setFetchedAt(moment());
    } catch {
      setError("Failed to fetch weather. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (val) => {
    if (val.length < 2) { setSuggestions([]); return; }
    try {
      const res  = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(val)}&count=5&language=en&format=json`);
      const data = await res.json();
      setSuggestions(data?.results || []);
    } catch { setSuggestions([]); }
  };

  useEffect(() => { fetchWeather(defaultLoc); }, []); // eslint-disable-line

  const cur   = weather?.current;
  const daily = weather?.daily;

  return (
    <DashCard>
      {/* Header */}
      <div style={{ ...hdrBase, borderRadius: "12px 12px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
          🌤️ Weather
          {fetchedAt && !loading && (
            <span style={{ fontSize: "0.62rem", fontWeight: 400, color: "rgba(255,255,255,0.65)", marginLeft: 8 }}>
              Updated {fetchedAt.format("h:mm A")}
            </span>
          )}
        </span>
        <button
          onClick={() => fetchWeather(inputVal)}
          disabled={loading}
          style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, color: "#fff", fontSize: "0.78rem", padding: "3px 10px", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
        >
          {loading ? "⟳ …" : "⟳ Refresh"}
        </button>
      </div>

      <div style={{ padding: "0.75rem" }}>
        {/* Search */}
        <div style={{ position: "relative", marginBottom: "0.75rem" }}>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <input
              value={inputVal}
              onChange={(e) => { setInputVal(e.target.value); fetchSuggestions(e.target.value); }}
              onKeyDown={(e) => { if (e.key === "Enter") fetchWeather(inputVal); }}
              placeholder="Search city / district…"
              style={{ ...inpSt, flex: 1 }}
            />
            <button
              onClick={() => fetchWeather(inputVal)}
              style={{ ...btnStyle, backgroundColor: C.mid, padding: "0.38rem 0.85rem", fontSize: "0.82rem" }}
            >
              Search
            </button>
          </div>

          {suggestions.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 80, zIndex: 20, backgroundColor: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", overflow: "hidden", marginTop: 2 }}>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  onClick={() => {
                    const label = [s.name, s.admin1, s.country].filter(Boolean).join(", ");
                    setInputVal(label);
                    setSuggestions([]);
                    fetchWeather(s.name);
                  }}
                  style={{ padding: "0.45rem 0.75rem", fontSize: "0.8rem", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? `1px solid ${C.border}` : "none", color: C.textDk }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = C.light}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#fff"}
                >
                  📍 {s.name}{s.admin1 ? `, ${s.admin1}` : ""}{s.country ? `, ${s.country}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div style={{ textAlign: "center", padding: "2rem", color: "#c62828", fontSize: "0.85rem" }}>{error}</div>
        )}

        {loading && !weather && (
          <div style={{ textAlign: "center", padding: "3rem", color: C.textGr, fontSize: "0.9rem" }}>⟳ Fetching weather…</div>
        )}

        {cur && location && (
          <>
            {/* Current conditions */}
            <div style={{ background: `linear-gradient(135deg, ${C.dark} 0%, ${C.mid} 100%)`, borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ fontSize: "3.8rem", lineHeight: 1, flexShrink: 0 }}>
                {wmoInfo(cur.weather_code).icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                  {location.name}{location.admin1 ? `, ${location.admin1}` : ""}
                </div>
                <div style={{ fontSize: "2.6rem", fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
                  {Math.round(cur.temperature_2m)}°C
                </div>
                <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.85)" }}>{wmoInfo(cur.weather_code).label}</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", marginTop: 1 }}>Feels like {Math.round(cur.apparent_temperature)}°C</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.55rem 1rem", fontSize: "0.72rem" }}>
                {[
                  { label: "💧 Humidity", value: `${cur.relative_humidity_2m}%` },
                  { label: "🌬️ Wind",     value: `${Math.round(cur.wind_speed_10m)} km/h ${degToDir(cur.wind_direction_10m)}` },
                  { label: "🌧️ Precip.",  value: `${cur.precipitation} mm` },
                  { label: "🌡️ Status",   value: cur.is_day ? "Daytime" : "Nighttime" },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ color: "rgba(255,255,255,0.55)", marginBottom: 1 }}>{label}</div>
                    <div style={{ fontWeight: 700, color: "#fff" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 5-day forecast */}
            {daily && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.4rem" }}>
                {daily.time.slice(0, 5).map((date, i) => {
                  const { icon: dIcon } = wmoInfo(daily.weather_code[i]);
                  const d = new Date(date);
                  return (
                    <div key={i} style={{ textAlign: "center", backgroundColor: i === 0 ? C.light : "#f8fdf9", border: `1px solid ${i === 0 ? C.mid : C.border}`, borderRadius: 10, padding: "0.55rem 0.25rem" }}>
                      <div style={{ fontSize: "0.62rem", fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {i === 0 ? "Today" : DAY_NAMES[d.getDay()]}
                      </div>
                      <div style={{ fontSize: "1.5rem", margin: "0.2rem 0" }}>{dIcon}</div>
                      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.textDk }}>{Math.round(daily.temperature_2m_max[i])}°</div>
                      <div style={{ fontSize: "0.65rem", color: C.textGr }}>{Math.round(daily.temperature_2m_min[i])}°</div>
                      {daily.precipitation_sum[i] > 0 && (
                        <div style={{ fontSize: "0.58rem", color: "#1e6fc4", marginTop: 2 }}>💧 {daily.precipitation_sum[i]}mm</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ textAlign: "center", paddingTop: "0.5rem", color: C.textGr, fontSize: "0.62rem", borderTop: `1px solid ${C.border}`, marginTop: "0.6rem" }}>
              Powered by Open-Meteo · auto-refreshes on search
            </div>
          </>
        )}
      </div>
    </DashCard>
  );
};

// ─── Market & Crop News (Live RSS via fetch — bypasses axios auth interceptor) ──
// Uses fetch() intentionally: the app's axios interceptor injects Authorization
// headers which rss2json.com blocks in its CORS preflight.
const RSS2JSON_BASE = "https://api.rss2json.com/v1/api.json?rss_url=";

const RSS_FEEDS = [
  {
    url: "https://economictimes.indiatimes.com/news/economy/agriculture/rssfeeds/11462317.cms",
    tag: "Agri",
    color: { bg: "#fff3e0", text: "#b76a00" },
  },
  {
    url: "https://www.agrifarming.in/feed",
    tag: "Farming",
    color: { bg: "#e8f5ee", text: "#2d7a45" },
  },
  {
    url: "https://krishijagran.com/rss/",
    tag: "Crops",
    color: { bg: "#fce4ec", text: "#c62828" },
  },
  {
    url: "https://news.google.com/rss/search?q=%E0%A4%B6%E0%A5%87%E0%A4%A4%E0%A5%80+%E0%A4%AA%E0%A5%80%E0%A4%95+%E0%A4%95%E0%A5%83%E0%A4%B7%E0%A5%80&hl=mr&gl=IN&ceid=IN:mr",
    tag: "मराठी",
    color: { bg: "#fff8e1", text: "#e65100" },
  },
  {
    url: "https://news.google.com/rss/search?q=%E0%A4%96%E0%A5%87%E0%A4%A4%E0%A5%80+%E0%A4%95%E0%A4%BF%E0%A4%B8%E0%A4%BE%E0%A4%A8+%E0%A4%AB%E0%A4%B8%E0%A4%B2&hl=hi&gl=IN&ceid=IN:hi",
    tag: "हिंदी",
    color: { bg: "#f3e5f5", text: "#7b1fa2" },
  },
];

const RSS_FALLBACK = AGRI_NEWS.map((n, i) => ({
  title: n.title,
  link: "#",
  pubDate: moment()
    .subtract(i * 2 + 1, "hours")
    .toISOString(),
  tag: n.tag,
  color: TAG_COLORS[n.tag] || { bg: "#f0f0f0", text: "#666" },
  source: "—",
}));

const cleanTitle = (t = "") =>
  t
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();

const extractImg = (html = "") => {
  const matches = [...(html || "").matchAll(/<img[^>]+src=["']?([^"'\s>]+)["']?/gi)];
  for (const m of matches) {
    const src = m[1];
    // skip tiny tracking pixels and data URIs
    if (src && !src.startsWith("data:") && !src.includes("pixel") && !src.includes("track") && src.length > 20) {
      return src;
    }
  }
  return null;
};

const fetchOgImage = (url) => {
  if (!url || url === "#") return Promise.resolve(null);
  return fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}&meta=false&video=false&audio=false`)
    .then((r) => r.json())
    .then((d) => d?.data?.image?.url || null)
    .catch(() => null);
};

const MarketTrendsCard = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled(
        RSS_FEEDS.map(({ url, tag, color }) =>
          fetch(`${RSS2JSON_BASE}${encodeURIComponent(url)}&num=5`)
            .then((r) => r.json())
            .then((data) => {
              if (data?.status !== "ok") return [];
              const source = data?.feed?.title || tag;
              return (data.items || []).map((item) => {
                const thumb =
                  (item.thumbnail && !item.thumbnail.startsWith("data:") ? item.thumbnail : null) ||
                  (item.enclosure?.type?.startsWith("image") ? item.enclosure.link : null) ||
                  extractImg(item.content) ||
                  extractImg(item.description) ||
                  null;
                return {
                  title: cleanTitle(item.title),
                  link: item.link || "#",
                  pubDate: item.pubDate || new Date().toISOString(),
                  thumbnail: thumb,
                  tag,
                  color,
                  source,
                };
              });
            }),
        ),
      );

      const merged = results
        .filter((r) => r.status === "fulfilled")
        .flatMap((r) => r.value)
        .filter((item) => item.title)
        .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

      const top10 = merged.slice(0, 10);

      // For articles without a thumbnail, fetch the OG image from the article page
      const withImages = await Promise.all(
        top10.map(async (item) => {
          if (item.thumbnail) return item;
          const ogImg = await fetchOgImage(item.link);
          return { ...item, thumbnail: ogImg };
        }),
      );

      setNews(withImages.length ? withImages : RSS_FALLBACK);
    } catch {
      setNews(RSS_FALLBACK);
    } finally {
      setFetchedAt(moment());
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
    const timer = setInterval(fetchFeeds, 10 * 60 * 1000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line

  return (
    <DashCard>
      <div
        style={{
          ...hdrBase,
          borderRadius: "12px 12px 0 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
          📰 Market Trends
          {fetchedAt && !loading && (
            <span
              style={{
                fontSize: "0.62rem",
                fontWeight: 400,
                color: "rgba(255,255,255,0.65)",
                marginLeft: 8,
              }}
            >
              Updated {fetchedAt.format("h:mm A")}
            </span>
          )}
        </span>
        <button
          onClick={fetchFeeds}
          disabled={loading}
          title="Refresh now"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: 6,
            color: "#fff",
            fontSize: "0.78rem",
            padding: "3px 10px",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? "⟳ …" : "⟳ Refresh"}
        </button>
      </div>

      <div style={{ padding: "0.6rem 0.75rem 0.75rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.38rem" }}>
          {loading && news.length === 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "3rem 1rem" }}>
              <div
                style={{
                  fontSize: "2rem",
                  marginBottom: "0.5rem",
                  opacity: 0.5,
                }}
              >
                ⟳
              </div>
              <div style={{ color: C.textGr, fontSize: "0.85rem" }}>
                Fetching live news…
              </div>
            </div>
          )}

          {!loading && news.length === 0 && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "2rem",
                color: C.textGr,
                fontSize: "0.85rem",
              }}
            >
              No articles available right now.
            </div>
          )}

          {news.slice(0, 10).map((item, i) => (
            <a
              key={i}
              href={item.link}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none", display: "flex", flexDirection: "column" }}
            >
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  borderLeft: `3px solid ${item.color.text}`,
                  backgroundColor: "#fafffe",
                  transition: "background 0.15s",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {item.thumbnail && (
                  <img
                    src={item.thumbnail}
                    alt=""
                    onError={(e) => { e.target.style.display = "none"; }}
                    style={{ width: "100%", height: 110, objectFit: "cover", display: "block", flexShrink: 0 }}
                  />
                )}
                <div style={{ padding: "0.5rem 0.65rem", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.4rem", marginBottom: "0.22rem" }}>
                      <span
                        style={{
                          fontSize: "0.79rem",
                          fontWeight: 600,
                          color: C.textDk,
                          lineHeight: 1.38,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.title}
                      </span>
                      <span
                        style={{
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          padding: "2px 7px",
                          borderRadius: 10,
                          backgroundColor: item.color.bg,
                          color: item.color.text,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        {item.tag}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <span style={{ fontSize: "0.63rem", color: C.textGr }}>
                      🕐 {moment(item.pubDate).fromNow()}
                    </span>
                    <span style={{ fontSize: "0.6rem", color: C.border }}>•</span>
                    <span
                      style={{
                        fontSize: "0.63rem",
                        color: C.textGr,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 120,
                      }}
                    >
                      {item.source}
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {news.length > 0 && (
          <div
            style={{
              textAlign: "center",
              paddingTop: "0.4rem",
              color: C.textGr,
              fontSize: "0.65rem",
              borderTop: `1px solid ${C.border}`,
              marginTop: "0.25rem",
            }}
          >
            Top 10 articles · English · मराठी · हिंदी · auto-refreshes every 10 min
          </div>
        )}
      </div>
    </DashCard>
  );
};

// ─── Upcoming Reminders Card ──────────────────────────────────────────────────
const urgencyColor = { immediateaction: C.red, moderatepriority: C.orange, lowpriority: "#2e7d32" };
const urgencyIcon  = { immediateaction: "🚫", moderatepriority: "⚠️", lowpriority: "🟩" };

const UpcomingRemindersCard = () => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [form, setForm] = useState({
    title: "",
    farmerName: "",
    farmerMobile: "",
    reminderDate: "",
    reminderTime: "",
    urgency: "moderatepriority",
  });

  const loadReminders = () => {
    setLoading(true);
    getRemindersApi().then((res) => {
      if (res?.status === 200) {
        setItems(res.data.map(r => ({ ...r, done: r.status === 'done' })));
      }
      setLoading(false);
    });
  };

  useEffect(() => { loadReminders(); }, []);

  const loggedUser = () => JSON.parse(localStorage.getItem("user") || "{}");

  const complete = (id) => {
    const u = loggedUser();
    setItems((prev) => prev.map((r) => r.id === id ? { ...r, done: true, status: 'done' } : r));
    completeReminderApi(id, { performedByUserId: String(u._id || u.id || ''), performedByName: u.fullName || '' });
  };

  const remove = (id) => {
    const u = loggedUser();
    setItems((prev) => prev.filter((r) => r.id !== id));
    deleteReminderApi(id, { performedByUserId: String(u._id || u.id || ''), performedByName: u.fullName || '' });
  };

  const reopen = (id) => {
    const u = loggedUser();
    setItems((prev) => prev.map((r) => r.id === id ? { ...r, done: false, status: 'pending' } : r));
    reopenReminderApi(id, { performedByUserId: String(u._id || u.id || ''), performedByName: u.fullName || '' });
  };

  const addReminder = () => {
    if (!form.title.trim() || saving) return;
    const u = loggedUser();
    setSaving(true);
    addReminderApi({
      title:           form.title.trim(),
      farmerName:      form.farmerName.trim(),
      farmerMobile:    form.farmerMobile.trim(),
      reminderDate:    form.reminderDate || null,
      reminderTime:    form.reminderTime || null,
      urgency:         form.urgency,
      createdByUserId: String(u._id || u.id || ''),
      createdByName:   u.fullName || '',
    }).then((res) => {
      setSaving(false);
      if (res?.status === 200) {
        setForm({ title: "", farmerName: "", farmerMobile: "", reminderDate: "", reminderTime: "", urgency: "moderatepriority" });
        setShowAdd(false);
        loadReminders();
      }
    });
  };

  const active = items.filter((r) => !r.done);
  const done   = items.filter((r) => r.done);

  return (
    <DashCard>
      <div
        style={{
          ...hdrBase,
          borderRadius: "12px 12px 0 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
          🔔 Upcoming Reminders
        </span>
        <button
          onClick={() => setShowAdd((v) => !v)}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: 6,
            color: "#fff",
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "3px 10px",
            cursor: "pointer",
          }}
        >
          {showAdd ? (
            "✕ Cancel"
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }} />
              Add
            </>
          )}
        </button>
      </div>

      <div style={{ padding: "0.75rem" }}>
        {/* Add form */}
        {showAdd && (
          <div
            style={{
              backgroundColor: "#f8fdf9",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "0.75rem",
              marginBottom: "0.75rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.4rem",
                marginBottom: "0.4rem",
              }}
            >
              <input
                placeholder="Reminder title *"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                style={{ ...inpSt, gridColumn: "1 / -1" }}
              />
              <input
                placeholder="Farmer name"
                value={form.farmerName}
                onChange={(e) => setForm({ ...form, farmerName: e.target.value })}
                style={inpSt}
              />
              <select
                value={form.urgency}
                onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                style={inpSt}
              >
                <option value="immediateaction">🚫 Immediate Action</option>
                <option value="moderatepriority">⚠️ Moderate Priority</option>
                <option value="lowpriority">🟩 Low Priority</option>
              </select>
              <input
                placeholder="Phone number"
                value={form.farmerMobile}
                onChange={(e) => setForm({ ...form, farmerMobile: e.target.value })}
                style={inpSt}
              />
              <input
                type="date"
                value={form.reminderDate}
                onChange={(e) => setForm({ ...form, reminderDate: e.target.value })}
                style={inpSt}
              />
              <input
                type="time"
                value={form.reminderTime}
                onChange={(e) => setForm({ ...form, reminderTime: e.target.value })}
                style={inpSt}
              />
            </div>
            <button
              onClick={addReminder}
              style={{
                ...btnStyle,
                width: "100%",
                backgroundColor: C.bright,
                fontSize: "0.78rem",
              }}
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "0.35rem" }} />
                  Saving…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: "0.35rem" }} />
                  Save Reminder
                </>
              )}
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: "center", padding: "1.5rem", color: C.textGr, fontSize: "0.85rem" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} />
            Loading reminders…
          </div>
        )}

        {/* Active list */}
        {!loading && (
          <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 2 }}>
            {active.length === 0 && (
              <div style={{ textAlign: "center", padding: "1.5rem", color: C.textGr, fontSize: "0.85rem" }}>
                No pending reminders.
              </div>
            )}
            {active.map((r) => (
              <div key={r.id}>
                <div
                  style={{
                    display: "flex",
                    gap: "0.6rem",
                    alignItems: "flex-start",
                    padding: "0.52rem 0.65rem",
                    marginBottom: expandedLog === r.id ? 0 : "0.38rem",
                    border: `1px solid ${C.border}`,
                    borderRadius: expandedLog === r.id ? "8px 8px 0 0" : 8,
                    borderLeft: `4px solid ${urgencyColor[r.urgency]}`,
                    backgroundColor:
                      r.urgency === "immediateaction" ? "#fff8f8"
                      : r.urgency === "moderatepriority" ? "#f8fff9"
                      : "#fafafa",
                  }}
                >
                  <div style={{ minWidth: 44, display: "flex", flexDirection: "column", alignItems: "center", gap: "0.15rem" }}>
                    <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>
                      {urgencyIcon[r.urgency] || "🔔"}
                    </span>
                    <div style={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.04em", color: urgencyColor[r.urgency], textTransform: "uppercase", textAlign: "center" }}>
                      {r.reminderDate ? moment(r.reminderDate).format("MM/DD") : "TBD"}
                    </div>
                    <div style={{ fontSize: "0.6rem", color: C.textGr }}>
                      {r.reminderTime || "—"}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.8rem", color: C.textDk, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.title}
                      </div>
                      <span style={{
                        fontSize: "0.6rem", fontWeight: 700, padding: "1px 7px", borderRadius: 8,
                        backgroundColor:
                          r.source === "feedback_followup" ? "#e3f2fd" :
                          r.source === "appointment"       ? "#fff3e0" :
                          r.source === "query"             ? "#f0fdf4" : "#e8f5ee",
                        color:
                          r.source === "feedback_followup" ? "#1565c0" :
                          r.source === "appointment"       ? "#e65100" :
                          r.source === "query"             ? "#166534" : C.mid,
                      }}>
                        {r.source === "feedback_followup" ? "Feedback" :
                         r.source === "appointment"       ? "Appointment" :
                         r.source === "query"             ? "Query" : "Manual"}
                      </span>
                    </div>
                    {r.farmerName && (
                      <div style={{ fontSize: "0.68rem", color: C.bright, marginTop: "0.1rem" }}>
                        👤 {r.farmerName}
                        {r.farmerMobile ? ` · 📞 ${r.farmerMobile}` : ""}
                      </div>
                    )}
                    {r.source === "query" && r.issueCode && (
                      <Link to={`/viewIssue/${r.issueCode}`} style={{ fontSize: "0.62rem", color: "#166534", marginTop: "0.1rem", display: "block" }}>
                        🔗 {r.issueCode}
                      </Link>
                    )}
                    <div style={{ fontSize: "0.62rem", color: C.textGr, marginTop: "0.1rem" }}>
                      By: {r.createdByName || "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                    <button
                      onClick={() => complete(r.id)}
                      style={{
                        background: "#e8f5ee", border: `1px solid ${C.border}`, borderRadius: 6,
                        color: C.bright, fontSize: "0.67rem", fontWeight: 700,
                        padding: "3px 7px", cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      <FontAwesomeIcon icon={faCheck} style={{ marginRight: 3 }} />
                      Done
                    </button>
                    <button
                      onClick={() => setExpandedLog(expandedLog === r.id ? null : r.id)}
                      style={{
                        background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                        color: C.textGr, fontSize: "0.67rem", padding: "3px 7px", cursor: "pointer",
                      }}
                      title="View history"
                    >
                      <FontAwesomeIcon icon={faHistory} style={{ fontSize: "0.65rem" }} />
                    </button>
                  </div>
                </div>
                {expandedLog === r.id && (
                  <div style={{
                    border: `1px solid ${C.border}`, borderTop: "none",
                    borderRadius: "0 0 8px 8px", padding: "0.5rem 0.75rem",
                    backgroundColor: "#f9fbfa", marginBottom: "0.38rem",
                  }}>
                    <div style={{ fontSize: "0.62rem", fontWeight: 700, color: C.mid, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Action History
                    </div>
                    {(r.logs || []).length === 0 && (
                      <div style={{ fontSize: "0.68rem", color: C.textGr }}>No log entries.</div>
                    )}
                    {(r.logs || []).map((log, i) => (
                      <div key={i} style={{ fontSize: "0.68rem", color: C.textGr, marginBottom: 2, display: "flex", gap: 6 }}>
                        <span style={{ fontWeight: 700, color: log.action === "done" ? "#2e7d32" : log.action === "deleted" ? C.red : C.mid, textTransform: "capitalize" }}>
                          {log.action}
                        </span>
                        <span>by {log.performedByName || "—"}</span>
                        <span>· {moment(log.createdAt).format("DD MMM, h:mm a")}</span>
                        {log.note && <span>· {log.note}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Completed */}
            {done.length > 0 && (
              <div style={{ marginTop: "0.5rem" }}>
                <div style={{ fontSize: "0.67rem", color: C.textGr, fontWeight: 600, marginBottom: "0.3rem" }}>
                  ✓ Completed ({done.length})
                </div>
                {done.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "0.38rem 0.65rem", marginBottom: "0.22rem",
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      backgroundColor: "#f5f5f5", opacity: 0.72,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: "0.75rem", color: C.textGr, textDecoration: "line-through",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block",
                      }}>
                        {r.title}
                      </span>
                      {r.createdByName && (
                        <span style={{ fontSize: "0.62rem", color: C.textGr }}>By: {r.createdByName}</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                      <button
                        onClick={() => reopen(r.id)}
                        title="Reopen — mark as not done"
                        style={{
                          background: "none", border: `1px solid ${C.border}`,
                          borderRadius: 5, color: C.mid,
                          fontSize: "0.68rem", cursor: "pointer",
                          padding: "2px 7px", display: "flex", alignItems: "center", gap: 4,
                        }}
                      >
                        <FontAwesomeIcon icon={faHistory} style={{ fontSize: "0.62rem" }} />
                        Reopen
                      </button>
                      <button
                        onClick={() => remove(r.id)}
                        title="Delete"
                        style={{
                          background: "none", border: "none", color: C.red,
                          fontSize: "0.75rem", cursor: "pointer",
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashCard>
  );
};

// ─── Quick Farmer Dialer ──────────────────────────────────────────────────────
const QuickDialerCard = ({ farmers, loading = false }) => {
  if (loading) return <DialerSkeleton />;
  const dialer = farmers.length
    ? farmers.slice(0, 6).map((f) => ({
        name: f.fullName
          ? f.fullName.split(" ")[0].substring(0, 8) +
            (f.fullName.split(" ").length > 1
              ? " " + f.fullName.split(" ").slice(-1)[0][0] + "."
              : "")
          : "-",
        status: "Active",
        mobile: f.mobileNumber,
        initials: f.fullName
          ? f.fullName
              .trim()
              .split(/\s+/)
              .map((w) => w[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)
          : "?",
      }))
    : MOCK_DIALER;

  return (
    <DashCard>
      <CardHdr title="Quick Farmer Dialer" icon="☎️" />
      <div style={{ padding: "0.75rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.5rem",
            marginBottom: "0.75rem",
          }}
        >
          {dialer.map((f, i) => (
            <a
              key={i}
              href={`tel:${f.mobile}`}
              style={{ textDecoration: "none" }}
            >
              <div
                style={{
                  textAlign: "center",
                  padding: "0.6rem 0.25rem",
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  backgroundColor: "#f9fdfb",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s",
                }}
              >
                {/* Avatar */}
                <div style={{ width: 42, margin: "0 auto 0.3rem" }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      backgroundColor: C.light,
                      border: `2px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.35rem",
                    }}
                  >
                    ☎️
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: C.textDk,
                    lineHeight: 1.2,
                  }}
                >
                  {f.name}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    marginTop: "0.2rem",
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 10,
                    backgroundColor:
                      f.status === "Busy" ? "#fff3e0" : "#e8f5ee",
                    color: f.status === "Busy" ? C.orange : C.bright,
                  }}
                >
                  {f.status}
                </div>
              </div>
            </a>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button style={{ ...btnStyle, flex: 1, backgroundColor: C.bright }}>
            <FontAwesomeIcon
              icon={faPhone}
              style={{ marginRight: "0.35rem", fontSize: "0.75rem" }}
            />
            Call Selected
          </button>
          <Link to="/farmer" style={{ flex: 1, textDecoration: "none" }}>
            <button
              style={{
                ...btnStyle,
                width: "100%",
                backgroundColor: "transparent",
                border: `1px solid ${C.border}`,
                color: C.textGr,
              }}
            >
              <FontAwesomeIcon
                icon={faUsers}
                style={{ marginRight: "0.35rem", fontSize: "0.75rem" }}
              />
              View All Farmers
            </button>
          </Link>
        </div>
      </div>
    </DashCard>
  );
};

// ─── Pending Issues Card ──────────────────────────────────────────────────────
const PendingIssuesCard = ({ issues, loading }) => (
  <DashCard>
    <div
      style={{
        ...hdrBase,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
        Pending Issues
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <Link
          to="/"
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            color: "#fff",
            textDecoration: "none",
            backgroundColor: C.bright,
          }}
        >
          SEE ALL
        </Link>
        <span>🔧</span>
      </div>
    </div>
    <div style={{ padding: "0.5rem" }}>
      {loading ? (
        <div style={{ padding: "0.35rem" }}>
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.6rem 0.75rem",
                  marginBottom: "0.35rem",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                }}
              >
                <Sh h={12} w={60} r={4} />
                <Sh h={12} sx={{ flex: 1 }} />
                <Sh h={20} w={78} r={10} />
              </div>
            ))}
        </div>
      ) : issues.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "1.5rem",
            color: C.textGr,
            fontSize: "0.85rem",
          }}
        >
          No pending issues found.
        </div>
      ) : (
        issues.map((issue) => {
          const st = STATUS_STYLE[issue.status] || {
            bg: "#f0f0f0",
            text: "#6c757d",
          };
          return (
            <Link
              key={issue._id || issue.issueCode}
              to={`/viewIssue/${issue.issueCode}`}
              style={{ textDecoration: "none" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.6rem 0.75rem",
                  marginBottom: "0.35rem",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  backgroundColor: "#fafffe",
                  transition: "background 0.15s",
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: C.mid,
                    minWidth: 60,
                  }}
                >
                  {issue.issueCode}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: "0.82rem",
                    color: C.textDk,
                    margin: "0 0.75rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {issue.title}
                </span>
                <span
                  style={{
                    fontSize: "0.66rem",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 10,
                    backgroundColor: st.bg,
                    color: st.text,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {STATUS_LABEL[issue.status] || issue.status?.toUpperCase()}
                </span>
              </div>
            </Link>
          );
        })
      )}
    </div>
  </DashCard>
);

// ─── Placeholder Card ─────────────────────────────────────────────────────────
const PlaceholderCard = ({
  title = "Coming Soon",
  icon = "🔧",
  message = "This feature is under development.",
}) => (
  <DashCard>
    <CardHdr title={title} icon={icon} />
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "3.5rem 1.5rem",
        gap: "0.75rem",
        minHeight: 220,
      }}
    >
      <div style={{ fontSize: "2.8rem", opacity: 0.25 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: "1rem", color: C.textGr }}>
        Coming Soon
      </div>
      <div
        style={{
          fontSize: "0.8rem",
          color: C.textGr,
          textAlign: "center",
          maxWidth: 240,
          lineHeight: 1.5,
        }}
      >
        {message}
      </div>
    </div>
  </DashCard>
);

// ─── Shared styles ────────────────────────────────────────────────────────────
const hdrBase = {
  backgroundColor: C.dark,
  color: "#fff",
  padding: "0.65rem 1rem",
};
const btnStyle = {
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "0.55rem 1rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  cursor: "pointer",
  transition: "opacity 0.15s",
};
const inpSt = {
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  padding: "0.38rem 0.6rem",
  fontSize: "0.8rem",
  outline: "none",
  width: "100%",
  backgroundColor: "#fff",
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [farmerCount, setFarmerCount] = useState(0);
  const [activeFarmerCount, setActiveFarmerCount] = useState(null);
  const [inactiveFarmerCount, setInactiveFarmerCount] = useState(null);
  const [appointmentCount, setAppointmentCount] = useState(null);
  const [done, setDone] = useState(0);
  const [solutionProv, setSolutionProv] = useState(0);
  const [inProgress, setInProgress] = useState(0);
  const [newIssues, setNewIssues] = useState(0);
  const [onHold, setOnHold] = useState(0);
  const [rejected, setRejected] = useState(0);
  const [inFollow, setInFollow] = useState(0);
  const [pendingIssues, setPendingIssues] = useState([]);
  const [recentFarmers, setRecentFarmers] = useState([]);
  const [monthlyFarmers, setMonthlyFarmers] = useState([]);
  const [monthlyQueries, setMonthlyQueries] = useState([]);
  const [monthlyAppointments, setMonthlyAppointments] = useState([]);
  const [statewiseFarmers, setStatewiseFarmers] = useState([]);
  const [cropStateData, setCropStateData] = useState([]);
  const [crops, setCrops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [loadingFarmers, setLoadingFarmers] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [loadingQueries, setLoadingQueries] = useState(true);

  // Yesterday counts
  const [ydActiveFarmers, setYdActiveFarmers] = useState(null);
  const [ydInactiveFarmers, setYdInactiveFarmers] = useState(null);
  const [ydQueries, setYdQueries] = useState(null);
  const [ydAppointments, setYdAppointments] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL;

    // Issue status counts
    const statuses = [
      "Done",
      "SolutionProvided",
      "In Progress",
      "New",
      "On Hold",
      "Rejected",
      "In Follow",
    ];
    const setters = [
      setDone,
      setSolutionProv,
      setInProgress,
      setNewIssues,
      setOnHold,
      setRejected,
      setInFollow,
    ];
    statuses.forEach((s, i) => {
      axios
        .get(`${API}/api/dashboard/issueCount/${s}`)
        .then((r) => {
          if (r.data?.count !== undefined) setters[i](r.data.count);
        })
        .catch(() => {});
    });

    // Farmer count
    axios
      .get(`${API}/api/dashboard/farmerCount`)
      .then((r) => {
        if (r.data?.farmerCount) setFarmerCount(r.data.farmerCount);
      })
      .catch(() => {});

    // Total appointments + monthly grouping
    axios
      .get(`${API}/api/appointment/getAppointments`)
      .then((r) => {
        const list = Array.isArray(r.data)
          ? r.data
          : Array.isArray(r.data?.appointments)
            ? r.data.appointments
            : [];
        setAppointmentCount(list.length);
        setMonthlyAppointments(groupByMonth(list, "createdAt", "count"));
        setLoadingAppointments(false);
      })
      .catch(() => {
        setLoadingAppointments(false);
      });

    // Yesterday query count
    const ydStr = moment().subtract(1, "days").format("YYYY-MM-DD");
    axios
      .get(
        `${API}/api/dashboard/issueCount/all?fromDate=${ydStr}&toDate=${ydStr}`,
      )
      .then((r) => {
        if (r.data?.count !== undefined) setYdQueries(r.data.count);
      })
      .catch(() => {});

    axios
      .get(
        `${API}/api/appointment/getAppointments?fromDate=${ydStr}&toDate=${ydStr}`,
      )
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.appointments;
        if (Array.isArray(list)) setYdAppointments(list.length);
      })
      .catch(() => {});

    // Farmer stats — aggregated on server, no bulk transfer
    axios
      .get(`${API}/api/dashboard/farmerStats`)
      .then((r) => {
        const d = r.data;
        setActiveFarmerCount(d.activeFarmers);
        setInactiveFarmerCount(d.inactiveFarmers);
        setYdActiveFarmers(d.ydActiveFarmers);
        setYdInactiveFarmers(d.ydInactiveFarmers);
        setMonthlyFarmers(d.monthlyFarmers || []);
        setStatewiseFarmers(d.statewiseFarmers || []);
        setCropStateData(d.cropStateData || []);
        setRecentFarmers(d.recentFarmers || []);
        setLoadingFarmers(false);
      })
      .catch(() => {
        setLoadingFarmers(false);
      });

    // Monthly query count (all issues, grouped by month)
    axios
      .post(`${API}/api/issue/searchIssue`, {})
      .then((r) => {
        const raw = r.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.issues)
            ? raw.issues
            : [];
        setMonthlyQueries(groupByMonth(list, "createdAt", "count"));
        setLoadingQueries(false);
      })
      .catch(() => {
        setLoadingQueries(false);
      });

    // Crops for search form
    axios
      .get(`${API}/api/crop/getCrops`)
      .then((r) => {
        if (Array.isArray(r.data)) setCrops(r.data);
      })
      .catch(() => {});

    // Users for search form
    axios
      .get(`${API}/api/user/getUsers`)
      .then((r) => {
        if (Array.isArray(r.data)) setUsers(r.data);
      })
      .catch(() => {});

    // Pending issues
    if (user._id) {
      getPendingIssues(user._id)
        .then((r) => {
          if (r?.data?.issues) setPendingIssues(r.data.issues.slice(0, 5));
          setLoadingIssues(false);
        })
        .catch(() => setLoadingIssues(false));
    } else {
      setLoadingIssues(false);
    }
  }, []);

  const solved = done + solutionProv;
  const pending = newIssues + inProgress + inFollow;
  const held = onHold + rejected;
  const today = moment().format("dddd, MMMM D, YYYY");

  const fmt = (n) => (n !== null && n !== undefined ? n.toLocaleString() : "—");

  const ydFarmerSub = (n, unit) =>
    n !== null && n !== undefined
      ? `${n > 0 ? "+" : ""}${n} ${unit} yesterday`
      : "Calculating…";

  const statCards = [
    {
      icon: faUsers,
      label: "Total Farmers",
      value: farmerCount ? farmerCount.toLocaleString() : "—",
      sub: `As of today · all registered farmers`,
      subIcon: faUsers,
      iconBg: "#e8f5ee",
    },
    {
      icon: faSeedling,
      label: "Active Farmers",
      value: fmt(activeFarmerCount),
      sub: ydFarmerSub(ydActiveFarmers, "farmers updated"),
      subIcon: faSeedling,
      trend:
        ydActiveFarmers > 0 ? "up" : ydActiveFarmers === 0 ? "flat" : undefined,
      iconBg: "#e8f5ee",
    },
    {
      icon: faUserSlash,
      label: "Inactive Farmers",
      value: fmt(inactiveFarmerCount),
      sub: ydFarmerSub(ydInactiveFarmers, "became inactive"),
      subIcon: faUserSlash,
      trend:
        ydInactiveFarmers > 0
          ? "down"
          : ydInactiveFarmers === 0
            ? "flat"
            : undefined,
      iconBg: "#fdecea",
      iconColor: C.red,
    },
    {
      icon: faQuestionCircle,
      label: "Crop Queries",
      value:
        fmt(
          newIssues +
            inProgress +
            inFollow +
            done +
            solutionProv +
            onHold +
            rejected,
        ) || "—",
      sub: ydFarmerSub(ydQueries, "queries raised"),
      subIcon: faQuestionCircle,
      trend: ydQueries > 0 ? "up" : ydQueries === 0 ? "flat" : undefined,
      iconBg: "#e8f0ff",
      iconColor: C.blue,
    },
    {
      icon: faCalendarAlt,
      label: "Total Appointments",
      value: fmt(appointmentCount),
      sub: ydFarmerSub(ydAppointments, "appointments"),
      subIcon: faCalendarAlt,
      trend:
        ydAppointments > 0 ? "up" : ydAppointments === 0 ? "flat" : undefined,
      iconBg: "#f0ede8",
      iconColor: C.gold,
    },
  ];

  return (
    <div
      style={{
        backgroundColor: C.dashbg,
        minHeight: "calc(100vh - 64px)",
        padding: "1.5rem 2rem 3rem",
      }}
    >
      <ShimmerStyle />
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: "0.85rem",
          marginBottom: "1.25rem",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <h3 style={{ fontWeight: 800, color: C.textDk, margin: 0 }}>
          Dashboard
        </h3>
        <span style={{ fontSize: "0.85rem", color: C.textGr }}>{today}</span>
      </div>

      {/* ── Stats Cards ── */}
      <Row className="g-3 mb-2">
        {statCards.map((c, i) => (
          <Col key={i} xs={12} sm={6} md={4} lg>
            {loadingFarmers ? <StatCardSkeleton /> : <StatCard {...c} />}
          </Col>
        ))}
      </Row>

      {/* ── Analytics ── */}
      <SectionHeader icon="📊" label="Analytics & Performance" />
      {/* Row 1: Monthly trends */}
      <Row className="g-3 mb-3">
        <Col xs={12} md={4}>
          <RegistrationTrendCard
            data={monthlyFarmers}
            loading={loadingFarmers}
          />
        </Col>
        <Col xs={12} md={4}>
          <MonthlyQueryCountCard
            data={monthlyQueries}
            loading={loadingQueries}
          />
        </Col>
        <Col xs={12} md={4}>
          <MonthlyAppointmentCard
            data={monthlyAppointments}
            loading={loadingAppointments}
          />
        </Col>
      </Row>
      {/* Row 2: Distributions */}
      <Row className="g-3 mb-2">
        <Col xs={12} md={4}>
          <CategoryDistributionCard />
        </Col>
        <Col xs={12} md={4}>
          <StatewiseFarmerCard
            data={statewiseFarmers}
            loading={loadingFarmers}
          />
        </Col>
        <Col xs={12} md={4}>
          <CropFarmerDataCard data={cropStateData} loading={loadingFarmers} />
        </Col>
      </Row>

      {/* ── Quick Tools ── */}
      <SectionHeader icon="🛠️" label="Quick Tools" />
      <Row className="g-3 mb-3">
        <Col xs={12} md={6}>
          <QuickDialerCard farmers={recentFarmers} loading={loadingFarmers} />
        </Col>
        <Col xs={12} md={6}>
          <UpcomingRemindersCard />
        </Col>
      </Row>

    </div>
  );
};

export default Dashboard;
